import { useFormik } from 'formik';
import { useCallback, useMemo } from 'react';
import { useCreateOrganization } from '@hooks/react-query/mutations/useCreateOrganization';
import {
  Button,
  Field,
  FieldTypes,
  Modal,
  SwitchWithDescriptionBox,
  Typography,
  useBreakpoints,
  useToast,
} from 'ui';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import {
  FormStack,
  SectionHeader,
  StyledButtonContent,
} from './CreateOrganizationModal.styles';
import {
  createOrganizationValidationSchema,
  FREE_QUOTA_DEFAULT,
  PRICE_PERCENTAGE_DEFAULT,
} from './utils';

interface CreateOrganizationFormValues {
  organizationName: string;
  hasParentOrg: boolean;
  parentOrganizationID: string;
}

interface CreateOrganizationModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function CreateOrganizationModal({
  open,
  onClose,
}: CreateOrganizationModalProps) {
  const { displayToaster } = useToast();
  const { isSmUp } = useBreakpoints();
  const { userOrganizations } = useUserOrgCase();
  const { mutateAsync: createOrganization, isPending } =
    useCreateOrganization();

  const organizationItems = useMemo(
    () =>
      userOrganizations.map(({ organizationID, organizationName }) => ({
        key: organizationID,
        value: organizationName,
      })),
    [userOrganizations],
  );

  const handleClose = useCallback(() => {
    formik.resetForm();
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  const onSubmit = async (values: CreateOrganizationFormValues) => {
    const { organizationName, hasParentOrg, parentOrganizationID } = values;

    try {
      await createOrganization({
        organizationName,
        organizationFreeQuota: FREE_QUOTA_DEFAULT,
        organizationPricePercentage: PRICE_PERCENTAGE_DEFAULT,
        ...(hasParentOrg && parentOrganizationID
          ? { parentOrganizationID }
          : {}),
      });
      displayToaster('Organization created successfully.', 'success');
      handleClose();
    } catch (error) {
      displayToaster('Organization could not be created.', 'error');
    }
  };

  const formik = useFormik<CreateOrganizationFormValues>({
    initialValues: {
      organizationName: '',
      hasParentOrg: false,
      parentOrganizationID: '',
    },
    validationSchema: createOrganizationValidationSchema,
    onSubmit,
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setValues,
  } = formik;

  const handleParentOrgToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setValues({
        ...values,
        hasParentOrg: checked,
        parentOrganizationID: checked ? values.parentOrganizationID : '',
      });
    },
    [setValues, values],
  );

  const isSubmitDisabled =
    !formik.dirty || Object.keys(formik.errors).length > 0;

  const actionButtons = (
    <StyledButtonContent>
      <Button
        fullWidth={!isSmUp}
        label="Cancel"
        onClick={handleClose}
        variant="outlined"
      />
      <Button
        disabled={isSubmitDisabled}
        fullWidth={!isSmUp}
        label="Create Organization"
        loading={isPending}
        loadingPosition="start"
        onClick={() => handleSubmit()}
        type="submit"
      />
    </StyledButtonContent>
  );

  return (
    <Modal
      actionButtons={actionButtons}
      modalClose={handleClose}
      onClose={handleClose}
      open={open}
      title="Create Organization"
    >
      <FormStack>
        <SectionHeader>
          <Typography variant="subtitle">
            <strong>New Organization</strong>
          </Typography>
          <Typography color="text.secondary" variant="caption">
            Add a name and optionally link a parent organization.
          </Typography>
        </SectionHeader>
        <Field
          errorMsg={
            (touched.organizationName && errors.organizationName) || undefined
          }
          label="Organization Name"
          name="organizationName"
          onBlur={handleBlur}
          onChange={handleChange}
          type={'text' as FieldTypes}
          value={values.organizationName}
        />
        <SwitchWithDescriptionBox
          checked={values.hasParentOrg}
          description="Link this organization to an existing parent."
          name="hasParentOrg"
          onChange={handleParentOrgToggle}
          title="Has Parent Organization"
        />
        {values.hasParentOrg ? (
          <Field
            errorMsg={
              (touched.parentOrganizationID && errors.parentOrganizationID) ||
              undefined
            }
            label="Parent Organization"
            menuItems={organizationItems}
            name="parentOrganizationID"
            onChange={handleChange}
            type={'select' as FieldTypes}
            value={values.parentOrganizationID}
          />
        ) : null}
      </FormStack>
    </Modal>
  );
}
